import type { ReactElement } from "react"
import { CURRENCY_SEPARATOR } from "@core/pricing"
import type { TextWithPrice as TextWithPriceType } from "@gatsby-plugin-generic-page/fragments/textWithPrice"
import getPriceBySquidexType from "@gatsby-plugin-generic-page/helpers/getPriceBySquidexType"
import Price from "@ionos-web-design-system/react/price"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import isEmpty from "~/lib/isEmpty"

type TextWithPriceProps = TextWithPriceType & {
  size?: "small" | "medium" | "large"
  hasFlatRenderMode?: boolean
}

export default function TextWithPrice({
  text,
  price,
  suffix,
  size = "large",
  hasFlatRenderMode,
}: TextWithPriceProps): ReactElement<TextWithPriceProps> | null {
  const { currentPriceType } = usePriceTypeContext()
  const priceData = getPriceBySquidexType(price, currentPriceType)

  if (isEmpty(priceData)) {
    return null
  }

  const resolvedPrefix = text || priceData.price.prefix
  const resolvedSuffix = suffix || priceData.price.suffix
  const pricePreline = !isEmpty(resolvedPrefix) ? resolvedPrefix : undefined

  return (
    <Price
      data={{
        prelines:
          pricePreline || priceData.price.discount
            ? {
                primary: {
                  preline: pricePreline,
                  discount: priceData.price.discount,
                },
              }
            : undefined,
        main: {
          comma: CURRENCY_SEPARATOR,
          currencySymbolPosition: "after",
          integerPrice: priceData.price.value.split(",")[0],
          decimalPrice: hasFlatRenderMode
            ? (priceData.price.value.split(",")[1] || "00").trim()
            : priceData.price.value.split(",")[1] || "00",
          currency: priceData.price.currency,
          additionalInfo: !isEmpty(resolvedSuffix) ? resolvedSuffix : undefined,
        },
      }}
      size={size}
      hasNoFixedSizes
      hasFlatRenderMode={hasFlatRenderMode}
      className="leading-normal"
    />
  )
}
